import { z } from "zod";

/** The RFC 6381 codec string of an AV1 video track: profile, level and tier, and bit depth. */
export const Av1Codecs = z
  .string()
  .regex(/^av01\.\d\.\d{2}[MH]\.\d{2}$/)
  .meta({
    id: "Av1Codecs",
    description:
      "The video track's RFC 6381 codec string, `av01.{profile}.{level}{tier}.{depth}`, such as `av01.0.08M.08`: AV1 Main profile, level 4.0, Main tier, 8-bit.",
    example: "av01.0.08M.08",
  });

/** The `type` a player checks before downloading an AV1 MP4. */
export function av1Mp4Type(codecs: string): string {
  return `video/mp4; codecs="${codecs}"`;
}

const Pixels = z.int().positive();

/** What a player reads about one video file before it downloads it. */
export const videoFileFields = {
  type: z.string().min(1).meta({
    description:
      "The file's type with its video codec, for a `<source>` element's `type` and for `navigator.mediaCapabilities`. It names only the video track, because some browsers skip a source whose `type` names an audio codec they play but don't report.",
    example: 'video/mp4; codecs="av01.0.08M.08"',
  }),
  width: Pixels.meta({ description: "The file's width in pixels.", example: 1920 }),
  height: Pixels.meta({ description: "The file's height in pixels.", example: 1080 }),
  frameRate: z.number().positive().meta({ description: "Frames per second.", example: 30 }),
  bitrate: z.int().positive().meta({
    description: "The video track's average bitrate, in bits per second.",
    example: 6_340_000,
  }),
  bytes: z.int().positive().meta({ description: "The file's size in bytes.", example: 9_812_004 }),
};

/** One size of a video as a URL a client can play. */
export interface VideoSetSource {
  url: string;
  type: string;
  width: number;
  height: number;
  frameRate: number;
  bitrate: number;
}

/** One video in one or more sizes, smallest first, as URLs a client can play. */
export interface VideoSet {
  sources: VideoSetSource[];
  durationSeconds: number;
  hasAudio: boolean;
}
